import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Checkbox,
  CheckboxGroup,
  FormControl, 
  FormLabel, 
  Input, 
  Stack,
  Heading,
  Text,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Grid,
  GridItem,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { useWeb3 } from '../lib/Web3Provider';
import { ROLES, ROLE_DESCRIPTIONS } from '../lib/roleDescriptions';
import { extractErrorMessage } from '../lib/utils';

interface RoleSelection {
  grant: boolean;
  disable: boolean;
}

const TokenCreationForm: React.FC = () => {
  const { address, writeContract, rolesLibrary } = useWeb3();
  const [maintainer, setMaintainer] = useState<string>('');
  const [roleSelections, setRoleSelections] = useState<Record<string, RoleSelection>>(
    ROLES.reduce((acc, role) => {
      acc[role] = { grant: false, disable: false };
      return acc;
    }, {} as Record<string, RoleSelection>)
  );
  const [creating, setCreating] = useState(false);
  const [tokenId, setTokenId] = useState<string | null>(null);
  const toast = useToast();

  const handleCheckboxChange = (role: string, type: 'grant' | 'disable', checked: boolean) => {
    setRoleSelections(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [type]: checked
      }
    }));
  };

  const handleCreate = async () => {
    if (!writeContract || !rolesLibrary) {
      toast({
        title: 'Error',
        description: 'Contract not loaded, please connect your wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setCreating(true);
    try {
      // Prepare role arrays
      const grants: number[] = [];
      const disables: number[] = [];

      // Map role names to their indices
      for (let i = 0; i < ROLES.length; i++) {
        const role = ROLES[i];
        const selection = roleSelections[role];
        
        if (selection.grant) {
          grants.push(i);
        }
        
        if (selection.disable) {
          disables.push(i);
        }
      }

      // Determine if maintainer is provided or default to connected wallet
      const maintainerAddress = maintainer.trim() ? maintainer : address;
      
      if (!maintainerAddress) {
        throw new Error('No maintainer address provided and no wallet connected');
      }

      // Create the token with role permissions
      const tx = await writeContract["create(address,uint8[],uint8[])"](
        maintainerAddress,
        grants,
        disables
      );

      // Wait for transaction confirmation
      const receipt = await tx.wait();
      
      // Extract token ID from event
      const event = receipt.events?.find(e => e.event === "Created");
      if (event && event.args && event.args.id) {
        const newTokenId = event.args.id.toString();
        setTokenId(newTokenId);
        toast({
          title: 'Token Created',
          description: `Successfully created token with ID: ${newTokenId}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('Created event not found in transaction receipt');
      }
    } catch (error) {
      console.error('Error creating token:', error);
      toast({
        title: 'Creation Failed',
        description: extractErrorMessage(error),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Heading as="h2" size="lg" mb={5}>
        Create New Token Type
      </Heading>
      
      <Text mb={4}>
        Each token type reservation mints one master token with specified role permissions.
        These roles can be assigned to third-parties for administration.
      </Text>
      
      {tokenId ? (
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          mb={5}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Token Created Successfully!
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            Your new token ID is: <b>{tokenId}</b>
            <br />
            You can now configure metadata and supply limits for this token.
          </AlertDescription>
          <Button 
            mt={4} 
            colorScheme="blue" 
            onClick={() => setTokenId(null)}
          >
            Create Another Token
          </Button>
        </Alert>
      ) : (
        <Box mb={5}>
          <FormControl mb={4}>
            <FormLabel>Maintainer Address (optional)</FormLabel>
            <Input 
              placeholder="Leave empty to use your connected address"
              value={maintainer}
              onChange={(e) => setMaintainer(e.target.value)}
            />
          </FormControl>
          
          <Grid templateColumns="repeat(1, 1fr)" gap={6} mb={6}>
            <GridItem>
              <Heading as="h3" size="md" mb={3}>
                Role Permissions
              </Heading>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Role</Th>
                    <Th>Description</Th>
                    <Th>Grant</Th>
                    <Th>Disable</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {ROLES.map((role, index) => {
                    // Skip the Reserved role (index 0)
                    if (index === 0) return null;
                    
                    return (
                      <Tr key={role}>
                        <Td fontWeight="medium">{role}</Td>
                        <Td>{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</Td>
                        <Td>
                          <Checkbox 
                            isChecked={roleSelections[role]?.grant}
                            onChange={(e) => handleCheckboxChange(role, 'grant', e.target.checked)}
                          />
                        </Td>
                        <Td>
                          <Checkbox 
                            isChecked={roleSelections[role]?.disable}
                            onChange={(e) => handleCheckboxChange(role, 'disable', e.target.checked)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </GridItem>
          </Grid>
          
          <Flex justifyContent="center" mt={8}>
            <Button 
              colorScheme="blue" 
              size="lg"
              isLoading={creating}
              loadingText="Creating Token..."
              onClick={handleCreate}
            >
              Create Token
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default TokenCreationForm;